import { Navbar } from '../components/Navbar';
import { Card, CardContent } from '../components/ui/card';
import { Shield, Heart, MessageSquare, AlertTriangle, Ban, Mail } from 'lucide-react';

export default function CodeOfConductPage() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="code-of-conduct-page">
      <Navbar />
      
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Shield className="w-6 h-6 text-indigo-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Code of Conduct</h1>
          <p className="text-slate-600">
            Our commitment to a safe and inclusive community
          </p>
        </div>

        <div className="prose-content space-y-8">
          {/* Pledge */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5 text-red-500" />
                Our Pledge
              </h2>
              <p className="text-slate-600 leading-relaxed">
                We as members, contributors, and leaders pledge to make participation in our 
                community a harassment-free experience for everyone, regardless of age, body 
                size, visible or invisible disability, ethnicity, sex characteristics, gender 
                identity and expression, level of experience, education, socio-economic status, 
                nationality, personal appearance, race, religion, or sexual identity and orientation.
              </p>
            </CardContent>
          </Card>

          {/* Expected Behavior */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-emerald-500" />
                Expected Behavior
              </h2>
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                  <span>Be respectful and considerate in all interactions</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                  <span>Give and gracefully accept constructive feedback</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                  <span>Focus on what is best for the community</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                  <span>Show empathy towards other community members</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                  <span>Help newcomers feel welcome and supported</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Unacceptable Behavior */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Unacceptable Behavior
              </h2>
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 flex-shrink-0" />
                  <span>Harassment, discrimination, or derogatory comments</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 flex-shrink-0" />
                  <span>Personal attacks or trolling</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 flex-shrink-0" />
                  <span>Publishing others' private information without consent</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 flex-shrink-0" />
                  <span>Spam, excessive self-promotion, or off-topic content</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 flex-shrink-0" />
                  <span>Any conduct that could reasonably be considered inappropriate</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Enforcement */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Ban className="w-5 h-5 text-red-500" />
                Enforcement
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Community leaders are responsible for clarifying and enforcing our standards of 
                acceptable behavior. They will take appropriate and fair corrective action in 
                response to any behavior that they deem inappropriate, threatening, offensive, 
                or harmful.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Violations may result in a warning, temporary ban, or permanent ban from the 
                community, depending on the severity of the violation.
              </p>
            </CardContent>
          </Card>

          {/* Reporting */}
          <Card className="bg-indigo-50 border-indigo-200">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-indigo-600" />
                Reporting
              </h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                If you experience or witness unacceptable behavior, please report it by:
              </p>
              <ul className="space-y-2 text-slate-700">
                <li>• Using the "Report" feature on any post or comment</li>
                <li>• Emailing us at <strong>conduct@pdxhacks.com</strong></li>
                <li>• Contacting a moderator directly</li>
              </ul>
              <p className="text-slate-600 text-sm mt-4">
                All reports will be handled confidentially.
              </p>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
